"use client";

import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useLang, useUi } from "@/components/ui/use-ui";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import { localePath } from "@/lib/i18n";
import { fmt } from "@/lib/locales/fmt";

// Thumbs up or down on a video by members (lib/comments.ts via /api/videos/<id>/rating), with the share
// that liked it. The page stays cached: the counts come from the browser.

interface Rating {
  up: number;
  down: number;
  mine: number;
  signedIn: boolean;
}

export function VideoRating({ id }: { id: string }) {
  const t = useUi();
  const lang = useLang();
  const router = useRouter();
  const [rating, setRating] = useState<Rating | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`/api/videos/${id}/rating`)
      .then((r) => (r.ok ? r.json() : null))
      .then((r: Rating | null) => live && r && setRating(r))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [id]);

  const rate = async (value: 1 | -1) => {
    if (!rating) return;
    if (!rating.signedIn) return router.push(localePath(lang, `/login?next=${encodeURIComponent(window.location.pathname)}`));
    const next = rating.mine === value ? 0 : value;
    // Shown at once, then as the server counts it.
    setRating({
      ...rating,
      mine: next,
      up: rating.up + (next === 1 ? 1 : 0) - (rating.mine === 1 ? 1 : 0),
      down: rating.down + (next === -1 ? 1 : 0) - (rating.mine === -1 ? 1 : 0),
    });
    const res = await fetch(`/api/videos/${id}/rating`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ value: next }) }).catch(() => null);
    if (res?.ok) setRating(await res.json());
  };

  const total = (rating?.up ?? 0) + (rating?.down ?? 0);
  const share = total ? Math.round(((rating?.up ?? 0) / total) * 100) : null;
  const hint = rating && !rating.signedIn ? t.comments.rateLogin : share !== null ? fmt(t.comments.liked, { p: share }) : undefined;
  const button = "flex h-[38px] items-center gap-1.5 px-3 text-[13px] tabular-nums transition-colors hover:bg-white/10 disabled:opacity-60";

  return (
    <div className="relative mr-1 flex items-center overflow-hidden rounded-full bg-white/[0.06]" title={hint}>
      <button
        type="button"
        onClick={() => rate(1)}
        disabled={!rating}
        aria-pressed={rating?.mine === 1}
        aria-label={`${t.comments.like}${rating ? ` (${rating.up})` : ""}`}
        className={cn(button, "pl-3.5", rating?.mine === 1 ? "text-fg" : "text-soft")}
      >
        <ThumbsUp size={18} strokeWidth={1.7} className={cn(rating?.mine === 1 && "fill-current")} />
        {rating ? compact(rating.up) : ""}
      </button>
      <span aria-hidden className="h-5 w-px bg-white/15" />
      <button
        type="button"
        onClick={() => rate(-1)}
        disabled={!rating}
        aria-pressed={rating?.mine === -1}
        aria-label={`${t.comments.dislike}${rating ? ` (${rating.down})` : ""}`}
        className={cn(button, "pr-3.5", rating?.mine === -1 ? "text-fg" : "text-soft")}
      >
        <ThumbsDown size={18} strokeWidth={1.7} className={cn(rating?.mine === -1 && "fill-current")} />
        {rating && rating.down > 0 ? compact(rating.down) : ""}
      </button>
      {share !== null && (
        <span aria-hidden className="absolute inset-x-3 bottom-0 h-[2px] overflow-hidden rounded-full bg-white/15">
          <span className="block h-full bg-brand" style={{ width: `${share}%` }} />
        </span>
      )}
    </div>
  );
}
